import React, { useEffect, useState } from 'react'

import { FmClearIcon } from '@octane/spark'
import getTrims, { GetTrimsOptions, Trim } from 'api/vehicles/getTrims'
import { FormikDropdown, FormikTextInput } from 'components/FormikFields'
import { FormikDropdownChild } from 'components/FormikFields/FormikDropdown/FormikDropdown'
import { FieldKit } from 'containers/FormBuilder'
import { FormikValues, getIn } from 'formik'

import {
    CancelManualEntryButton,
    ManualEntryButton,
    TextInputWrapper,
} from './dropdowns.styled'

interface TrimDropdownProps {
    formik: FieldKit<FormikValues>
    prefilled?: boolean
    onAutomobileSelected?: (automobileId: number) => void
    onTextInputMode?: (active: boolean) => void
    availableAutomobileIds?: number[]
}

const defaultProps = {
    prefilled: false,
    onAutomobileSelected: undefined,
    onTextInputMode: undefined,
    availableAutomobileIds: undefined,
}

export default function TrimDropdown({
    formik,
    prefilled,
    onAutomobileSelected,
    onTextInputMode,
    availableAutomobileIds,
}: Readonly<TrimDropdownProps>) {
    const [allTrims, setAllTrims] = useState([] as Trim[])
    const [loading, setLoading] = useState(false)
    const [manualEntry, setManualEntry] = useState(false)
    const [refreshKey, setRefreshKey] = useState(0)
    const { vehicle } = formik.values
    const { condition, year, makeId, modelId, automobileId } = vehicle
    const isTextInput = manualEntry

    // Recomputed whenever raw trims or VIN-filter IDs change — no re-fetch needed
    const response = availableAutomobileIds?.length
        ? allTrims.filter((t) =>
              availableAutomobileIds.includes(t.automobile_id),
          )
        : allTrims

    useEffect(() => {
        if (modelId && makeId && year) {
            onTextInputMode?.(isTextInput)
        } else {
            onTextInputMode?.(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTextInput, modelId, makeId, year])

    function cancelManualEntry() {
        formik.setFieldValue('vehicle.trim', '')
        formik.setFieldValue('vehicle.automobileId', '')
        setRefreshKey((k) => k + 1)
    }

    useEffect(() => {
        const abortController = new AbortController()

        setManualEntry(false)

        if (condition && year && makeId && modelId) {
            const options: GetTrimsOptions = {
                condition,
                year,
                makeId,
                modelId,
            }

            setAllTrims([])
            setLoading(true)

            getTrims(options, abortController)
                .then((trims: Trim[]) => {
                    setAllTrims(trims)
                    setLoading(false)
                })
                .catch(() => {
                    setLoading(false)
                })
        }

        return () => abortController.abort()
    }, [condition, year, makeId, modelId, refreshKey])

    // Auto-select runs whenever raw trims load or VIN-filter IDs change
    useEffect(() => {
        if (loading || response.length === 0) return

        const currentTrim = response.find(
            (t) => t.automobile_id.toString() === automobileId,
        )

        if (response.length === 1) {
            const newAutoId = response[0].automobile_id.toString()
            const changed = newAutoId !== automobileId

            formik.setFieldValue('vehicle.automobileId', newAutoId)
            formik.setFieldValue('vehicle.trim', response[0].trim)

            if (changed) {
                onAutomobileSelected?.(response[0].automobile_id)
            }
        } else if (currentTrim) {
            formik.setFieldValue('vehicle.trim', currentTrim.trim)
        } else {
            const trimByName = vehicle.trim
                ? response.find((t) => t.trim === vehicle.trim)
                : null

            if (trimByName) {
                formik.setFieldValue(
                    'vehicle.automobileId',
                    trimByName.automobile_id.toString(),
                )

                onAutomobileSelected?.(trimByName.automobile_id)
            } else {
                formik.setFieldValue('vehicle.automobileId', '')
                formik.setFieldValue('vehicle.trim', '')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allTrims, availableAutomobileIds])

    if (!modelId || !makeId || !year) {
        return (
            <FormikDropdown
                name="vehicle.automobileId"
                label="Trim"
                placeholder="Select One"
                disabled
                prefilled={prefilled}
            >
                {[{ name: '', value: '' }]}
            </FormikDropdown>
        )
    }

    if (isTextInput) {
        return (
            <TextInputWrapper>
                <FormikTextInput
                    name="vehicle.trim"
                    label="Trim"
                    prefilled={prefilled}
                />
                {!(
                    getIn(formik.errors, 'vehicle.trim') &&
                    getIn(formik.touched, 'vehicle.trim')
                ) && (
                    <CancelManualEntryButton
                        type="button"
                        aria-label="Cancel manual entry"
                        onClick={cancelManualEntry}
                    >
                        <FmClearIcon height={24} />
                    </CancelManualEntryButton>
                )}
            </TextInputWrapper>
        )
    }

    return (
        <FormikDropdown
            name="vehicle.automobileId"
            label="Trim"
            placeholder="Select One"
            disabled={loading || response.length === 0}
            error={!loading && (automobileId === null || automobileId === '')}
            searchable
            searchPlaceholder="Search trims..."
            prefilled={prefilled}
            noMatchComponent={
                <ManualEntryButton
                    type="button"
                    onClick={() => {
                        formik.setFieldValue('vehicle.automobileId', '')
                        formik.setFieldValue('vehicle.trim', '')
                        setManualEntry(true)
                    }}
                >
                    Add trim manually
                </ManualEntryButton>
            }
            onChange={(val: string) => {
                const selected = response.find(
                    (t) => t.automobile_id.toString() === val,
                )

                if (selected) {
                    formik.setFieldValue('vehicle.trim', selected.trim)
                    onAutomobileSelected?.(selected.automobile_id)
                }
            }}
        >
            {response.map(
                (t: Trim): FormikDropdownChild => ({
                    name: t.trim,
                    value: t.automobile_id.toString(),
                }),
            )}
        </FormikDropdown>
    )
}

TrimDropdown.defaultProps = defaultProps
